// ChoralEditorialDetail.jsx
import React, { useCallback, useEffect, useState } from "react";
import ChoralIdentityPanel from "./ChoralIdentityPanel";
import ChoralLiturgicalPanel from "./ChoralLiturgicalPanel";
import ChoralMusicalPanel from "./ChoralMusicalPanel";
import ChoralGovernancePanel from "./ChoralGovernancePanel";
import AISuggestionsPanel from "./AISuggestionsPanel";
import ChoralAuditTimeline from "./ChoralAuditTimeline";

export default function ChoralEditorialDetail({
  workId,
  apiBase = "/api/admin/choral",
  canVerify = false,
  readOnly = false,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!workId) { setData(null); return; }
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${apiBase}/${workId}`, { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (e) {
      setError(e.message);
    } finally { setLoading(false); }
  }, [workId, apiBase]);

  useEffect(() => { load(); }, [load]);

  if (!workId) {
    return (
      <div className="ce-detail" data-testid="ce-detail-empty">
        <div className="ce-detail__empty">
          Select a work from the list to open its editorial dossier.
        </div>
      </div>
    );
  }
  if (loading) {
    return (
      <div className="ce-detail" data-testid="ce-detail-loading">
        <div className="ce-detail__empty">Loading…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="ce-detail" data-testid="ce-detail-error">
        <div className="ce-detail__empty">Failed to load record: {error}</div>
      </div>
    );
  }
  if (!data) return null;

  const { work, audit, suggestions } = data;

  return (
    <div className="ce-detail" data-testid={`ce-detail-${work.id}`}>
      <h1 className="ce-detail__title" data-testid="ce-detail-title">{work.title}</h1>
      <p className="ce-detail__composer" data-testid="ce-detail-composer">
        {work.composer || "Anonymous"}
      </p>

      <div className="ce-detail__grid">
        <ChoralIdentityPanel   work={work} readOnly={readOnly} onSaved={load} apiBase={apiBase} />
        <ChoralLiturgicalPanel work={work} readOnly={readOnly} onSaved={load} apiBase={apiBase} />
        <ChoralMusicalPanel    work={work} />
        <ChoralGovernancePanel
          work={work}
          canVerify={canVerify}
          onChanged={load}
          apiBase={apiBase}
          readOnly={readOnly}
        />
      </div>

      <AISuggestionsPanel
        workId={work.id}
        suggestions={suggestions || []}
        onDecided={load}
        apiBase={apiBase}
      />

      <ChoralAuditTimeline audit={audit || []} />
    </div>
  );
}
